import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUpload from "@/components/ImageUpload";
import MediaUpload from "@/components/MediaUpload";
import ChatPanel from "@/components/ChatPanel";
import CostItemsEditor from "@/components/CostItemsEditor";
import { Download, ExternalLink, Check } from "lucide-react";

const STATUSES = ["In attesa", "In lavorazione", "Preventivo inviato", "Confermato", "Rifiutato"];

// Sanitize URLs: allow only http(s), reject javascript:/data:/vbscript: etc.
function safeHttpUrl(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (low.startsWith("http://") || low.startsWith("https://")) return s;
  if (!/[:\s]/.test(s.slice(0, 20))) return "https://" + s;
  return "";
}
const statusColor = {
  "In attesa": "bg-[#801B2C]/25 text-[#E5A93C] border-[#E5A93C]/40",
  "In lavorazione": "bg-[#E5A93C]/20 text-[#E5A93C] border-[#E5A93C]/60",
  "Preventivo inviato": "bg-[#F7F4EE]/10 text-[#F7F4EE] border-[#F7F4EE]/30",
  Confermato: "bg-[#2f4a2b]/40 text-emerald-300 border-emerald-400/40",
  Rifiutato: "bg-[#3a1c1c]/60 text-red-300 border-red-400/40",
};

function StatsCard({ label, value, testid }) {
  return (
    <div className="av-card p-5" data-testid={testid}>
      <div className="text-[#A39E93] text-xs uppercase tracking-widest">{label}</div>
      <div className="font-serif-display text-4xl mt-2 text-[#F7F4EE]">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [artists, setArtists] = useState([]);
  const [shows, setShows] = useState([]);
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [visits, setVisits] = useState(null);
  const [social, setSocial] = useState([]);
  const [disc, setDisc] = useState([]);

  const reload = async () => {
    const [s, r, a, sh, c, d, ap, sp, mp, vs, so] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/quote-requests"),
      api.get("/artists"),
      api.get("/shows"),
      api.get("/admin/clients"),
      api.get("/disciplines"),
      api.get("/artist-applications"),
      api.get("/admin/sponsors"),
      api.get("/admin/media-partners"),
      api.get("/admin/visits"),
      api.get("/admin/social-links"),
    ]);
    setStats(s.data);
    setRequests(r.data);
    setArtists(a.data);
    setShows(sh.data);
    setClients(c.data);
    setDisc(d.data);
    setApplications(ap.data);
    setSponsors(sp.data);
    setPartners(mp.data);
    setVisits(vs.data);
    setSocial(so.data);
  };

  useEffect(() => {
    if (user && user.role === "admin") reload();
  }, [user]);

  if (!ready) return <div className="pt-28 text-center text-[#A39E93]">Caricamento…</div>;
  if (!user || user.role !== "admin")
    return <Navigate to="/login" state={{ from: "/admin" }} replace />;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quote-requests/${id}/status`, { status });
      toast.success("Stato aggiornato");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow">Area gestione NMA</span>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3">
          Sala <span className="italic text-[#E5A93C]">regia</span>
        </h1>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
            <StatsCard label="Richieste totali" value={stats.requests_total} testid="stat-total" />
            <StatsCard label="In attesa" value={stats.requests_pending} testid="stat-pending" />
            <StatsCard label="In lavorazione" value={stats.requests_working} testid="stat-working" />
            <StatsCard label="Confermate" value={stats.requests_confirmed} testid="stat-confirmed" />
          </div>
        )}

        <Tabs defaultValue="requests" className="mt-10">
          <TabsList className="bg-[#141210] border av-hairline flex-wrap">
            <TabsTrigger value="requests" data-testid="tab-requests">Richieste</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Candidature</TabsTrigger>
            <TabsTrigger value="artists" data-testid="tab-artists">Artisti</TabsTrigger>
            <TabsTrigger value="shows" data-testid="tab-shows">Spettacoli</TabsTrigger>
            <TabsTrigger value="clients" data-testid="tab-clients">Enti / Clienti</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors">Sponsor</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Media partner</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">Social hub</TabsTrigger>
            <TabsTrigger value="visits" data-testid="tab-visits">Visite</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="mt-6 space-y-4">
            {requests.map((r) => (
              <div key={r.id} className="av-card p-5" data-testid={`request-${r.id}`}>
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div>
                    <h3 className="font-serif-display text-2xl">{r.event_name}</h3>
                    <p className="text-sm text-[#A39E93]">
                      {r.city} {r.venue && `· ${r.venue}`} {r.event_date && `· ${r.event_date}`}
                    </p>
                    <p className="text-sm text-[#F7F4EE]/80 mt-1">
                      {r.contact_name} · {r.contact_email} {r.contact_phone && `· ${r.contact_phone}`}
                      {r.organization && ` · ${r.organization}`}
                    </p>
                  </div>
                  <span
                    className={`text-xs uppercase tracking-widest border rounded-full px-3 py-1 ${statusColor[r.status] || ""}`}
                    data-testid={`request-status-${r.id}`}
                  >
                    {r.status}
                  </span>
                </div>
                {r.notes && (
                  <p className="text-sm text-[#F7F4EE]/70 mt-3 whitespace-pre-line">{r.notes}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {r.artists.map((a) => (
                    <span key={a.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                      🎤 {a.name}
                    </span>
                  ))}
                  {r.shows.map((s) => (
                    <span key={s.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                      🎭 {s.title}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(r.id, s)}
                      className={`text-xs av-pill ${r.status === s ? "av-pill-gold" : "av-pill-ghost"}`}
                      data-testid={`set-status-${r.id}-${s.replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                  <a
                    href={`${API}/quote-requests/${r.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs av-pill av-pill-ghost ml-auto"
                    data-testid={`download-pdf-${r.id}`}
                  >
                    <Download size={12} /> Scarica PDF
                  </a>
                  {r.share_token && (
                    <a
                      href={`/preventivo/${r.share_token}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs av-pill av-pill-ghost"
                      data-testid={`open-public-${r.id}`}
                    >
                      <ExternalLink size={12} /> Anteprima cliente
                    </a>
                  )}
                </div>
                <ChatPanel requestId={r.id} currentUserRole="admin" />
                <CostItemsEditor
                  request={r}
                  onUpdated={(fresh) =>
                    setRequests((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)))
                  }
                />
                {r.signed_at && (
                  <div className="mt-4 border-l-2 border-emerald-400 pl-3" data-testid={`signed-${r.id}`}>
                    <div className="av-eyebrow text-[10px]" style={{ color: "#7DD3A0" }}>
                      <Check size={10} className="inline mr-1" /> Firmato dal cliente
                    </div>
                    <p className="text-sm text-[#F7F4EE]/85 mt-1">
                      {r.signed_by} · {new Date(r.signed_at).toLocaleString("it-IT")}
                    </p>
                  </div>
                )}
              </div>
            ))}
            {!requests.length && <p className="text-[#A39E93]">Nessuna richiesta ancora.</p>}
          </TabsContent>

          <TabsContent value="artists" className="mt-6">
            <CatalogManager
              kind="artists"
              disciplines={disc}
              items={artists}
              onReload={reload}
            />
          </TabsContent>
          <TabsContent value="shows" className="mt-6">
            <CatalogManager
              kind="shows"
              disciplines={disc}
              items={shows}
              onReload={reload}
            />
          </TabsContent>

          <TabsContent value="clients" className="mt-6">
            <div className="av-card overflow-hidden">
              <table className="w-full text-sm" data-testid="clients-table">
                <thead className="text-[#A39E93] text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Nome</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Ente</th>
                    <th className="text-left px-4 py-3">Telefono</th>
                    <th className="text-left px-4 py-3">Iscritto</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className="border-t av-hairline">
                      <td className="px-4 py-3">{c.name}</td>
                      <td className="px-4 py-3">{c.email}</td>
                      <td className="px-4 py-3">{c.organization}</td>
                      <td className="px-4 py-3">{c.phone}</td>
                      <td className="px-4 py-3 text-[#A39E93]">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : ""}
                      </td>
                    </tr>
                  ))}
                  {!clients.length && (
                    <tr>
                      <td colSpan={5} className="text-center text-[#A39E93] py-6">
                        Nessun cliente registrato.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="applications" className="mt-6 space-y-4" data-testid="applications-list">
            {applications.map((a) => (
              <div key={a.id} className="av-card p-5" data-testid={`application-${a.id}`}>
                <div className="flex flex-wrap justify-between gap-3 items-start">
                  <div className="flex items-start gap-4">
                    {a.photo_url && (
                      <img src={a.photo_url} alt={a.name} className="w-14 h-14 object-cover rounded-full border av-hairline" />
                    )}
                    <div>
                      <h3 className="font-serif-display text-2xl">
                        {a.stage_name || a.name}
                        {a.is_emerging && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#E5A93C] border border-[#E5A93C]/50 rounded-full px-2 py-0.5">
                            Emergente
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-[#A39E93]">
                        {a.discipline}
                        {a.city ? ` · ${a.city}` : ""}
                        {a.experience_years ? ` · ${a.experience_years} anni` : ""}
                      </p>
                      <p className="text-sm text-[#F7F4EE]/80 mt-1">
                        {a.name} · {a.email} {a.phone && `· ${a.phone}`}
                      </p>
                    </div>
                  </div>
                  <span
                    className="text-xs uppercase tracking-widest border rounded-full px-3 py-1 text-[#E5A93C] border-[#E5A93C]/40"
                    data-testid={`app-status-${a.id}`}
                  >
                    {a.status}
                  </span>
                </div>
                {a.bio && <p className="text-sm text-[#F7F4EE]/75 mt-3 whitespace-pre-line">{a.bio}</p>}
                <div className="flex flex-wrap gap-3 mt-3 text-xs">
                  {a.demo_url && safeHttpUrl(a.demo_url) && (
                    <a href={safeHttpUrl(a.demo_url)} target="_blank" rel="noreferrer" className="text-[#E5A93C] hover:underline">
                      🎧 Demo
                    </a>
                  )}
                  {a.portfolio_url && safeHttpUrl(a.portfolio_url) && (
                    <a href={safeHttpUrl(a.portfolio_url)} target="_blank" rel="noreferrer" className="text-[#E5A93C] hover:underline">
                      📁 Portfolio
                    </a>
                  )}
                  {a.social_urls && (
                    <span className="text-[#A39E93]">📱 {a.social_urls}</span>
                  )}
                  {a.availability && (
                    <span className="text-[#A39E93]">📅 {a.availability}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {["Ricevuta", "In valutazione", "Contattato", "Ammesso", "Non idoneo"].map((s) => (
                    <button
                      key={s}
                      onClick={async () => {
                        try {
                          await api.patch(`/artist-applications/${a.id}`, { status: s, admin_note: a.admin_note || "" });
                          toast.success("Stato aggiornato");
                          reload();
                        } catch (err) {
                          toast.error(formatApiError(err));
                        }
                      }}
                      className={`text-xs av-pill ${a.status === s ? "av-pill-gold" : "av-pill-ghost"}`}
                      data-testid={`app-set-status-${a.id}-${s.replace(/\s+/g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!applications.length && (
              <p className="text-[#A39E93]">Nessuna candidatura ricevuta.</p>
            )}
          </TabsContent>

          <TabsContent value="sponsors" className="mt-6">
            <SponsorsManager items={sponsors} onReload={reload} />
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <PartnersManager items={partners} onReload={reload} />
          </TabsContent>

          <TabsContent value="social" className="mt-6">
            <SocialManager items={social} onReload={reload} />
          </TabsContent>

          <TabsContent value="visits" className="mt-6">
            <VisitsPanel data={visits} />
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

function CatalogManager({ kind, disciplines, items, onReload }) {
  const isArtists = kind === "artists";
  const [form, setForm] = useState(
    isArtists
      ? { name: "", discipline: disciplines[0] || "", city: "", bio: "", image_url: "", video_url: "", tags: "", price_from: "" }
      : { title: "", category: disciplines[0] || "", duration_min: "", description: "", image_url: "", tags: "", price_from: "" }
  );
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        price_from: form.price_from ? Number(form.price_from) : null,
      };
      if (!isArtists) payload.duration_min = form.duration_min ? Number(form.duration_min) : null;
      await api.post(isArtists ? "/artists" : "/shows", payload);
      toast.success(isArtists ? "Artista aggiunto" : "Spettacolo aggiunto");
      onReload();
      setForm({ ...form, ...(isArtists ? { name: "", bio: "", city: "" } : { title: "", description: "" }) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Confermi la rimozione?")) return;
    await api.delete(`/${kind}/${id}`);
    toast.success("Rimosso");
    onReload();
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6">
      <form onSubmit={submit} className="av-card p-5 h-max space-y-3" data-testid={`${kind}-form`}>
        <h3 className="font-serif-display text-xl">
          {isArtists ? "Nuovo artista" : "Nuovo spettacolo"}
        </h3>
        <div>
          <label className="av-label">{isArtists ? "Nome" : "Titolo"} *</label>
          <input
            required
            className="av-input"
            value={isArtists ? form.name : form.title}
            onChange={upd(isArtists ? "name" : "title")}
            data-testid={`${kind}-name-input`}
          />
        </div>
        <div>
          <label className="av-label">{isArtists ? "Disciplina" : "Categoria"}</label>
          <select
            className="av-input"
            value={isArtists ? form.discipline : form.category}
            onChange={upd(isArtists ? "discipline" : "category")}
          >
            {disciplines.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        {isArtists ? (
          <div>
            <label className="av-label">Città</label>
            <input className="av-input" value={form.city} onChange={upd("city")} />
          </div>
        ) : (
          <div>
            <label className="av-label">Durata (min)</label>
            <input
              type="number"
              className="av-input"
              value={form.duration_min}
              onChange={upd("duration_min")}
            />
          </div>
        )}
        <div>
          <label className="av-label">Immagine</label>
          <ImageUpload
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
            testid={`${kind}-image-upload`}
          />
        </div>
        {isArtists && (
          <div>
            <label className="av-label">Video demo</label>
            <MediaUpload
              kind="video"
              value={form.video_url}
              onChange={(v) => setForm({ ...form, video_url: v })}
              testid={`${kind}-video-upload`}
            />
          </div>
        )}
        <div>
          <label className="av-label">Prezzo indicativo (€)</label>
          <input
            type="number"
            className="av-input"
            value={form.price_from}
            onChange={upd("price_from")}
          />
        </div>
        <div>
          <label className="av-label">Tag (virgola)</label>
          <input className="av-input" value={form.tags} onChange={upd("tags")} />
        </div>
        <div>
          <label className="av-label">{isArtists ? "Bio" : "Descrizione"}</label>
          <textarea
            rows={4}
            className="av-input"
            value={isArtists ? form.bio : form.description}
            onChange={upd(isArtists ? "bio" : "description")}
          />
        </div>
        <button className="av-pill av-pill-gold w-full justify-center" data-testid={`${kind}-add-btn`}>
          Aggiungi
        </button>
      </form>

      <div className="space-y-3" data-testid={`${kind}-list`}>
        {items.map((it) => (
          <div key={it.id} className="av-card p-4 flex gap-4 items-center">
            {it.image_url && (
              <img
                src={it.image_url}
                alt=""
                className="w-16 h-16 object-cover rounded-md"
              />
            )}
            <div className="flex-1">
              <h4 className="font-serif-display text-lg">
                {isArtists ? it.name : it.title}
              </h4>
              <p className="text-xs text-[#A39E93]">
                {isArtists ? it.discipline : it.category}
                {isArtists && it.city ? ` · ${it.city}` : ""}
                {!isArtists && it.duration_min ? ` · ${it.duration_min} min` : ""}
                {it.price_from ? ` · da € ${it.price_from.toLocaleString()}` : ""}
              </p>
            </div>
            <button
              onClick={() => remove(it.id)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`${kind}-delete-${it.id}`}
            >
              Elimina
            </button>
          </div>
        ))}
        {!items.length && <p className="text-[#A39E93]">Vuoto.</p>}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Sponsors manager
// ---------------------------------------------------------------------------
function SponsorsManager({ items, onReload }) {
  const [form, setForm] = useState({
    name: "",
    image_url: "",
    link: "",
    active: true,
    sort_order: 0,
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.image_url) {
      toast.error("Nome e logo sono obbligatori");
      return;
    }
    try {
      await api.post("/admin/sponsors", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success("Sponsor aggiunto");
      setForm({ name: "", image_url: "", link: "", active: true, sort_order: 0 });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/admin/sponsors/${s.id}`, { ...s, active: !s.active });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo sponsor?")) return;
    await api.delete(`/admin/sponsors/${id}`);
    toast.success("Sponsor rimosso");
    onReload();
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6">
      <form
        onSubmit={submit}
        className="av-card p-5 h-max space-y-3"
        data-testid="sponsor-form"
      >
        <h3 className="font-serif-display text-xl">Nuovo sponsor</h3>
        <div>
          <label className="av-label">Nome / brand *</label>
          <input
            required
            className="av-input"
            value={form.name}
            onChange={upd("name")}
            data-testid="sponsor-name-input"
          />
        </div>
        <div>
          <label className="av-label">Logo / immagine *</label>
          <ImageUpload
            value={form.image_url}
            onChange={(v) => setForm({ ...form, image_url: v })}
            testid="sponsor-image-upload"
          />
        </div>
        <div>
          <label className="av-label">Link (opzionale)</label>
          <input
            className="av-input"
            placeholder="https://..."
            value={form.link}
            onChange={upd("link")}
            data-testid="sponsor-link-input"
          />
        </div>
        <div>
          <label className="av-label">Ordine</label>
          <input
            type="number"
            className="av-input"
            value={form.sort_order}
            onChange={upd("sort_order")}
          />
        </div>
        <button
          className="av-pill av-pill-gold w-full justify-center"
          data-testid="sponsor-add-btn"
        >
          Aggiungi sponsor
        </button>
      </form>

      <div className="space-y-3" data-testid="sponsors-list">
        {items.map((s) => (
          <div
            key={s.id}
            className="av-card p-4 flex gap-4 items-center"
            data-testid={`sponsor-row-${s.id}`}
          >
            <img
              src={s.image_url}
              alt={s.name}
              className="w-24 h-16 object-contain bg-[#0a0908] rounded"
            />
            <div className="flex-1">
              <h4 className="font-serif-display text-lg">{s.name}</h4>
              {s.link && (
                <p className="text-xs text-[#A39E93] truncate max-w-md">
                  {s.link}
                </p>
              )}
              <p className="text-xs text-[#A39E93]">
                Ordine: {s.sort_order} ·{" "}
                <span className={s.active ? "text-emerald-300" : "text-red-300"}>
                  {s.active ? "Attivo" : "Nascosto"}
                </span>
              </p>
            </div>
            <button
              onClick={() => toggle(s)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`sponsor-toggle-${s.id}`}
            >
              {s.active ? "Disattiva" : "Attiva"}
            </button>
            <button
              onClick={() => remove(s.id)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`sponsor-delete-${s.id}`}
            >
              Elimina
            </button>
          </div>
        ))}
        {!items.length && (
          <p className="text-[#A39E93]">
            Nessuno sponsor ancora. La home mostrerà "Spazio disponibile".
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Media partners manager
// ---------------------------------------------------------------------------
function PartnersManager({ items, onReload }) {
  const [form, setForm] = useState({
    name: "",
    logo_url: "",
    link: "",
    active: true,
    sort_order: 0,
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.logo_url) {
      toast.error("Nome e logo sono obbligatori");
      return;
    }
    try {
      await api.post("/admin/media-partners", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success("Media partner aggiunto");
      setForm({ name: "", logo_url: "", link: "", active: true, sort_order: 0 });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/media-partners/${p.id}`, {
        ...p,
        active: !p.active,
      });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo media partner?")) return;
    await api.delete(`/admin/media-partners/${id}`);
    toast.success("Rimosso");
    onReload();
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-6">
      <form
        onSubmit={submit}
        className="av-card p-5 h-max space-y-3"
        data-testid="partner-form"
      >
        <h3 className="font-serif-display text-xl">Nuovo media partner</h3>
        <div>
          <label className="av-label">Nome testata *</label>
          <input
            required
            className="av-input"
            value={form.name}
            onChange={upd("name")}
            data-testid="partner-name-input"
          />
        </div>
        <div>
          <label className="av-label">Logo *</label>
          <ImageUpload
            value={form.logo_url}
            onChange={(v) => setForm({ ...form, logo_url: v })}
            testid="partner-logo-upload"
          />
        </div>
        <div>
          <label className="av-label">Link (opzionale)</label>
          <input
            className="av-input"
            placeholder="https://..."
            value={form.link}
            onChange={upd("link")}
            data-testid="partner-link-input"
          />
        </div>
        <div>
          <label className="av-label">Ordine</label>
          <input
            type="number"
            className="av-input"
            value={form.sort_order}
            onChange={upd("sort_order")}
          />
        </div>
        <button
          className="av-pill av-pill-gold w-full justify-center"
          data-testid="partner-add-btn"
        >
          Aggiungi partner
        </button>
      </form>

      <div className="space-y-3" data-testid="partners-list">
        {items.map((p) => (
          <div
            key={p.id}
            className="av-card p-4 flex gap-4 items-center"
            data-testid={`partner-row-${p.id}`}
          >
            <img
              src={p.logo_url}
              alt={p.name}
              className="w-24 h-16 object-contain bg-[#0a0908] rounded"
            />
            <div className="flex-1">
              <h4 className="font-serif-display text-lg">{p.name}</h4>
              {p.link && (
                <p className="text-xs text-[#A39E93] truncate max-w-md">
                  {p.link}
                </p>
              )}
              <p className="text-xs text-[#A39E93]">
                Ordine: {p.sort_order} ·{" "}
                <span className={p.active ? "text-emerald-300" : "text-red-300"}>
                  {p.active ? "Attivo" : "Nascosto"}
                </span>
              </p>
            </div>
            <button
              onClick={() => toggle(p)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`partner-toggle-${p.id}`}
            >
              {p.active ? "Disattiva" : "Attiva"}
            </button>
            <button
              onClick={() => remove(p.id)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`partner-delete-${p.id}`}
            >
              Elimina
            </button>
          </div>
        ))}
        {!items.length && (
          <p className="text-[#A39E93]">Nessun media partner ancora.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visits panel (admin only)
// ---------------------------------------------------------------------------
function VisitsPanel({ data }) {
  if (!data)
    return <p className="text-[#A39E93]">Caricamento contatore visite…</p>;
  const maxDaily = Math.max(1, ...data.daily.map((d) => d.count));
  return (
    <div className="space-y-6" data-testid="visits-panel">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatsCard
          label="Visite totali"
          value={data.total_visits.toLocaleString("it-IT")}
          testid="visits-total"
        />
        <StatsCard
          label="Oggi"
          value={data.today.toLocaleString("it-IT")}
          testid="visits-today"
        />
        <StatsCard
          label="Visitatori unici (IP)"
          value={data.unique_ips.toLocaleString("it-IT")}
          testid="visits-unique"
        />
      </div>

      <div className="av-card p-5">
        <h3 className="font-serif-display text-xl">Ultimi 30 giorni</h3>
        <div className="flex items-end gap-1 mt-4 h-32">
          {[...data.daily].reverse().map((d) => (
            <div
              key={d.day}
              className="flex-1 min-w-0 group relative"
              title={`${d.day}: ${d.count} visite`}
            >
              <div
                className="bg-[#E5A93C]/80 hover:bg-[#E5A93C] rounded-t transition-colors"
                style={{ height: `${(d.count / maxDaily) * 100}%` }}
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-[#A39E93] mt-3">
          Barre = visite giornaliere · Passa sopra per il dettaglio
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="av-card p-5">
          <h3 className="font-serif-display text-xl">Pagine più visitate</h3>
          <ul className="mt-3 space-y-2 text-sm" data-testid="visits-top-paths">
            {data.top_paths.map((p) => (
              <li
                key={p.path}
                className="flex justify-between border-b av-hairline pb-2"
              >
                <code className="text-[#E5A93C]">{p.path}</code>
                <span className="text-[#F7F4EE]">{p.count}</span>
              </li>
            ))}
            {!data.top_paths.length && (
              <li className="text-[#A39E93]">Nessuna visita registrata.</li>
            )}
          </ul>
        </div>
        <div className="av-card p-5">
          <h3 className="font-serif-display text-xl">Ultime visite</h3>
          <ul className="mt-3 space-y-2 text-xs" data-testid="visits-recent">
            {data.recent.map((r, i) => (
              <li key={`${r.created_at || "v"}-${r.path || ""}-${i}`} className="border-b av-hairline pb-2">
                <div className="flex justify-between">
                  <code className="text-[#E5A93C]">{r.path}</code>
                  <span className="text-[#A39E93]">
                    {r.created_at
                      ? new Date(r.created_at).toLocaleString("it-IT")
                      : ""}
                  </span>
                </div>
                {r.ref && (
                  <div className="text-[#A39E93]/80 truncate">← {r.ref}</div>
                )}
              </li>
            ))}
            {!data.recent.length && (
              <li className="text-[#A39E93]">Nessuna visita.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Social hub manager
// ---------------------------------------------------------------------------
const SOCIAL_PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "youtube", label: "YouTube" },
  { key: "tiktok", label: "TikTok" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "twitter", label: "Twitter / X" },
  { key: "website", label: "Sito web" },
];

function SocialManager({ items, onReload }) {
  const [form, setForm] = useState({
    platform: "facebook",
    name: "",
    url: "",
    cover_image: "",
    latest_post: "",
    latest_post_url: "",
    embed_auto: true,
    active: true,
    sort_order: 0,
  });
  const upd = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url) {
      toast.error("Nome e URL sono obbligatori");
      return;
    }
    try {
      await api.post("/admin/social-links", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success("Link social aggiunto");
      setForm({
        platform: "facebook",
        name: "",
        url: "",
        cover_image: "",
        latest_post: "",
        latest_post_url: "",
        embed_auto: true,
        active: true,
        sort_order: 0,
      });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/admin/social-links/${s.id}`, {
        ...s,
        active: !s.active,
      });
      onReload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Rimuovere questo link social?")) return;
    await api.delete(`/admin/social-links/${id}`);
    toast.success("Rimosso");
    onReload();
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-6">
      <form
        onSubmit={submit}
        className="av-card p-5 h-max space-y-3"
        data-testid="social-form"
      >
        <h3 className="font-serif-display text-xl">Nuovo canale social</h3>
        <div>
          <label className="av-label">Piattaforma</label>
          <select
            className="av-input"
            value={form.platform}
            onChange={upd("platform")}
            data-testid="social-platform-select"
          >
            {SOCIAL_PLATFORMS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="av-label">Nome pagina / canale *</label>
          <input
            required
            className="av-input"
            value={form.name}
            onChange={upd("name")}
            placeholder="es. NMA Agency"
            data-testid="social-name-input"
          />
        </div>
        <div>
          <label className="av-label">URL della pagina *</label>
          <input
            required
            className="av-input"
            value={form.url}
            onChange={upd("url")}
            placeholder="https://www.facebook.com/NMAagency"
            data-testid="social-url-input"
          />
        </div>
        {form.platform === "facebook" && (
          <label className="flex items-center gap-2 text-sm text-[#F7F4EE]/85 cursor-pointer">
            <input
              type="checkbox"
              checked={form.embed_auto}
              onChange={upd("embed_auto")}
              data-testid="social-embed-auto"
            />
            Feed automatico (Facebook Page Plugin — mostra da solo il post più recente)
          </label>
        )}
        <div>
          <label className="av-label">Anteprima / cover (opzionale)</label>
          <ImageUpload
            value={form.cover_image}
            onChange={(v) => setForm({ ...form, cover_image: v })}
            testid="social-cover-upload"
          />
        </div>
        <div>
          <label className="av-label">Caption ultimo post (opzionale)</label>
          <textarea
            rows={3}
            className="av-input"
            value={form.latest_post}
            onChange={upd("latest_post")}
            placeholder="Copia qui il testo del post più recente"
          />
        </div>
        <div>
          <label className="av-label">Link diretto al post (opzionale)</label>
          <input
            className="av-input"
            value={form.latest_post_url}
            onChange={upd("latest_post_url")}
            placeholder="https://..."
          />
        </div>
        <div>
          <label className="av-label">Ordine</label>
          <input
            type="number"
            className="av-input"
            value={form.sort_order}
            onChange={upd("sort_order")}
          />
        </div>
        <button
          className="av-pill av-pill-gold w-full justify-center"
          data-testid="social-add-btn"
        >
          Aggiungi canale
        </button>
      </form>

      <div className="space-y-3" data-testid="social-list">
        {items.map((s) => (
          <div
            key={s.id}
            className="av-card p-4 flex gap-4 items-center"
            data-testid={`social-row-${s.id}`}
          >
            {s.cover_image ? (
              <img
                src={s.cover_image}
                alt={s.name}
                className="w-24 h-16 object-cover rounded"
              />
            ) : (
              <div className="w-24 h-16 rounded bg-[#0a0908] flex items-center justify-center text-[#A39E93] text-xs">
                {s.platform}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h4 className="font-serif-display text-lg truncate">
                {s.name}
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[#E5A93C]">
                  {s.platform}
                  {s.platform === "facebook" && s.embed_auto ? " · auto" : ""}
                </span>
              </h4>
              <p className="text-xs text-[#A39E93] truncate">{s.url}</p>
              {s.latest_post && (
                <p className="text-xs text-[#F7F4EE]/70 truncate italic mt-1">
                  “{s.latest_post}”
                </p>
              )}
              <p className="text-xs text-[#A39E93] mt-1">
                Ordine: {s.sort_order} ·{" "}
                <span className={s.active ? "text-emerald-300" : "text-red-300"}>
                  {s.active ? "Attivo" : "Nascosto"}
                </span>
              </p>
            </div>
            <button
              onClick={() => toggle(s)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`social-toggle-${s.id}`}
            >
              {s.active ? "Disattiva" : "Attiva"}
            </button>
            <button
              onClick={() => remove(s.id)}
              className="text-xs av-pill av-pill-ghost"
              data-testid={`social-delete-${s.id}`}
            >
              Elimina
            </button>
          </div>
        ))}
        {!items.length && (
          <p className="text-[#A39E93]">
            Nessun canale social ancora. Suggerimenti: NMA Agency FB, Gruppo
            Casting FB, Instagram, YouTube.
          </p>
        )}
      </div>
    </div>
  );
}

